import React, { useEffect, useState } from "react";
import { Control, FieldValues, Path, useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "@monorepo/components";
import {
  finalIbanRegex,
  formatIbanModel,
  formatIbanView,
  getIbanViewLength,
  getISO,
  ibanMatchesSpacingRegex,
  tooLongIbanRegex,
} from "@monorepo/helpers";

type TIbanInputProps<TFieldValues extends FieldValues> = {
  defaultValue?: { key?: string; value: string };
  onBlurAction?: () => void;
  responseError?: any;
  disabled?: boolean;
  registerName?: string;
  control: Control<TFieldValues>;
};

export function IbanInput<TFieldValues extends FieldValues>({
  responseError,
  defaultValue,
  onBlurAction,
  disabled,
  registerName,

  control,
}: TIbanInputProps<TFieldValues>) {
  const { setError, formState, setValue } = useFormContext();
  const [fieldName] = useState<string>(registerName ?? "iban");
  const { t } = useTranslation();
  const {
    field: { ref, name, ...fieldProps },
    fieldState: { error },
  } = useController({
    name: fieldName as Path<TFieldValues>,
    control,
    rules: {
      required: {
        value: true,
        message: t("common.required"),
      },
      validate: {
        ibanFormat: (value) => finalIbanRegex(value as string) || (t("services.must_follow_iban_format") as string),
      },
    },
  });

  const [ibanViewValue, setIbanViewValue] = useState(defaultValue ? formatIbanView(defaultValue.value) : getISO());

  useEffect(() => {
    if (responseError) {
      setError(name, { type: "backend", message: responseError });
    }
  }, [name, responseError, setError]);

  useEffect(() => {
    if (defaultValue) {
      setValue(fieldName, formatIbanModel(defaultValue.value));
      setIbanViewValue(formatIbanView(defaultValue.value));
    }
  }, [defaultValue, fieldName, setValue]);

  useEffect(() => {
    if (
      ibanViewValue &&
      tooLongIbanRegex(ibanViewValue) &&
      formatIbanView(ibanViewValue).length === getIbanViewLength()
    ) {
      if (onBlurAction) {
        onBlurAction();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ibanViewValue]);

  const handleIbanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (tooLongIbanRegex(value) && formatIbanView(value).length === getIbanViewLength()) {
      setIbanViewValue((prevState) => {
        if (formatIbanView(value) === formatIbanView(prevState)) {
          return value;
        } else return formatIbanView(value);
      });
      setValue(fieldName, formatIbanModel(value), { shouldValidate: true, shouldTouch: true });
      return;
    }
    // matches LT XX XXXX XXXX XXXX XXXX and LTXXXXXXXXXXXXXXXXXX and in-between spacing
    if (!ibanMatchesSpacingRegex(value)) {
      if (formatIbanView(value).length <= getIbanViewLength()) {
        setError(fieldName, {
          type: "manual",
          message: t("services.must_follow_iban_format"),
        });
      }
      return;
    }
    setIbanViewValue(value);
    setValue(fieldName, formatIbanModel(value), {
      shouldValidate:
        formState.touchedFields[fieldName] || e.target.value.length === getIbanViewLength() ? true : false,
    });
  };

  return (
    <Input
      {...fieldProps}
      name={name}
      label={t("services.iban")}
      value={ibanViewValue}
      readOnly={disabled}
      onChange={handleIbanChange}
      ref={ref}
      error={error?.message || undefined}
    />
  );
}
